import type { Address } from 'viem'

/**
 * Maximum number of sub-accounts per owner in EVC
 */
export const MAX_SUB_ACCOUNTS = 256

/**
 * Default sub-account ID (main account)
 */
export const MAIN_ACCOUNT_ID = 0

/**
 * Fallback subaccount range to always scan (0-15).
 * This ensures positions are visible even before discovery completes,
 * covering the most common subaccount IDs users typically use.
 */
export const FALLBACK_SUBACCOUNT_RANGE = 16

/**
 * Compute a sub-account address from an owner address and sub-account ID.
 *
 * In EVC, sub-accounts are derived by XOR-ing the owner address with a number 0-255.
 * All 256 sub-accounts share the same first 19 bytes (address prefix), differing only
 * in the last byte.
 *
 * @param owner - The owner's main wallet address
 * @param subAccountId - The sub-account ID (0-255). 0 = main account.
 * @returns The sub-account address
 *
 * @example
 * // Main account (subAccountId = 0) returns the same address
 * getSubAccountAddress('0x1234...5678', 0) // '0x1234...5678'
 *
 * // Sub-account 1 XORs the last byte
 * getSubAccountAddress('0x1234...5678', 1) // '0x1234...5679'
 */
export function getSubAccountAddress(owner: Address, subAccountId: number): Address {
  if (subAccountId < 0 || subAccountId > 255) {
    throw new Error(`Sub-account ID must be between 0 and 255, got ${subAccountId}`)
  }

  // Sub-account 0 is the main account (no XOR needed)
  if (subAccountId === 0) {
    return owner
  }

  // Convert address to BigInt, XOR with subAccountId, convert back to address
  const ownerBigInt = BigInt(owner)
  const subAccountBigInt = ownerBigInt ^ BigInt(subAccountId)

  // Pad to 40 hex characters (20 bytes) and add 0x prefix
  return `0x${subAccountBigInt.toString(16).padStart(40, '0')}` as Address
}

/**
 * Extract the sub-account ID from a sub-account address given the owner address.
 *
 * @param owner - The owner's main wallet address
 * @param subAccount - The sub-account address to check
 * @returns The sub-account ID (0-255), or null if not a valid sub-account of this owner
 */
export function getSubAccountId(owner: Address, subAccount: Address): number | null {
  const ownerBigInt = BigInt(owner)
  const subAccountBigInt = BigInt(subAccount)

  // XOR to get the sub-account ID
  const subAccountId = ownerBigInt ^ subAccountBigInt

  // Valid sub-account IDs are 0-255 (fit in a single byte)
  if (subAccountId < 0n || subAccountId > 255n) {
    return null
  }

  return Number(subAccountId)
}

/**
 * Get the address prefix (first 19 bytes) shared by all sub-accounts of an owner.
 *
 * @param address - Any address (owner or sub-account)
 * @returns The 19-byte address prefix as a hex string
 */
export function getAddressPrefix(address: Address): `0x${string}` {
  // Remove '0x' prefix, take first 38 hex chars (19 bytes), add '0x' back
  return `0x${address.slice(2, 40)}` as `0x${string}`
}

/**
 * Check if two addresses share the same owner (same address prefix).
 *
 * @param address1 - First address
 * @param address2 - Second address
 * @returns True if both addresses belong to the same owner
 */
export function haveCommonOwner(address1: Address, address2: Address): boolean {
  return getAddressPrefix(address1) === getAddressPrefix(address2)
}

/**
 * Generate all sub-account addresses for an owner.
 *
 * @param owner - The owner's main wallet address
 * @param count - Number of sub-accounts to generate (default: 256 for all)
 * @returns Array of sub-account addresses
 */
export function getAllSubAccountAddresses(owner: Address, count: number = MAX_SUB_ACCOUNTS): Address[] {
  const addresses: Address[] = []
  const actualCount = Math.min(count, MAX_SUB_ACCOUNTS)

  for (let i = 0; i < actualCount; i++) {
    addresses.push(getSubAccountAddress(owner, i))
  }

  return addresses
}

/**
 * Sub-account information including address and ID
 */
export interface SubAccountInfo {
  address: Address
  subAccountId: number
  isMainAccount: boolean
}

/**
 * Get sub-account info for a given owner and sub-account ID.
 *
 * @param owner - The owner's main wallet address
 * @param subAccountId - The sub-account ID (0-255)
 * @returns SubAccountInfo object
 */
export function getSubAccountInfo(owner: Address, subAccountId: number): SubAccountInfo {
  return {
    address: getSubAccountAddress(owner, subAccountId),
    subAccountId,
    isMainAccount: subAccountId === MAIN_ACCOUNT_ID,
  }
}
